export type IconType = 'stage' | 'admin' | 'default';

interface IconConfig {
  type: IconType;
  sizes: number[];
  title?: string;
}

const routeIconMap: Record<string, IconConfig> = {
  // Home route
  '/': { type: 'default', sizes: [120, 152, 167, 180], title: 'Happi Screen' },
  // Stage routes
  '/stage': {
    type: 'stage',
    sizes: [120, 152, 167, 180],
    title: 'Happi Stage',
  },
  // Admin routes
  '/admin': {
    type: 'admin',
    sizes: [120, 152, 167, 180],
    title: 'Happi Admin',
  },
};

export function updateAppleTouchIcons(pathname: string): void {
  // Find the matching route configuration
  let iconConfig: IconConfig = {
    type: 'default',
    sizes: [120, 152, 167, 180],
    title: 'Happi Screen',
  };

  // Check for exact matches first
  if (routeIconMap[pathname]) {
    iconConfig = routeIconMap[pathname];
  } else {
    // Check for partial matches (for dynamic routes)
    const pathSegments = pathname.split('/');
    for (const [route, config] of Object.entries(routeIconMap)) {
      const routeSegments = route.split('/');

      // Match if the route segments match up to the shorter length
      const minLength = Math.min(pathSegments.length, routeSegments.length);
      const pathMatch = pathSegments.slice(0, minLength).join('/');
      const routeMatch = routeSegments.slice(0, minLength).join('/');

      if (pathMatch === routeMatch && routeMatch !== '') {
        iconConfig = config;
        break;
      }
    }
  }

  // Update all apple-touch-icon links
  iconConfig.sizes.forEach((size) => {
    const link = document.querySelector(
      `link[rel="apple-touch-icon"][sizes="${size}x${size}"]`,
    ) as HTMLLinkElement;
    if (link) {
      link.href = `/icons/apple-${iconConfig.type}-icon-${size}x${size}.png`;
    }
  });

  // Update the home screen title
  if (iconConfig.title) {
    // Update the document title
    document.title = iconConfig.title;

    // Update the apple-mobile-web-app-title meta tag
    let appleTitleMeta = document.querySelector(
      'meta[name="apple-mobile-web-app-title"]',
    ) as HTMLMetaElement;
    if (!appleTitleMeta) {
      appleTitleMeta = document.createElement('meta');
      appleTitleMeta.name = 'apple-mobile-web-app-title';
      document.head.appendChild(appleTitleMeta);
    }
    appleTitleMeta.content = iconConfig.title;

    // Update the application-name meta tag
    let appNameMeta = document.querySelector(
      'meta[name="application-name"]',
    ) as HTMLMetaElement;
    if (!appNameMeta) {
      appNameMeta = document.createElement('meta');
      appNameMeta.name = 'application-name';
      document.head.appendChild(appNameMeta);
    }
    appNameMeta.content = iconConfig.title;
  }

  // Update manifest icons dynamically
  updateManifestIcons();
}

function updateManifestIcons(): void {
  // This function would update the manifest.json file dynamically
  // However, since manifest.json is static, we'll update the manifest link
  // to point to a dynamic endpoint if needed in the future

  // For now, we'll just ensure the manifest is properly linked
  let manifestLink = document.querySelector(
    'link[rel="manifest"]',
  ) as HTMLLinkElement;
  if (!manifestLink) {
    manifestLink = document.createElement('link');
    manifestLink.rel = 'manifest';
    manifestLink.href = '/manifest.json';
    document.head.appendChild(manifestLink);
  }
}

// Helper function to get the current pathname
export function getCurrentPathname(): string {
  return window.location.pathname;
}
